import type { ReactNode } from "react";
import type { SubmissionBrief, SubmissionView } from "@/app/reviewQueries";
import { cellAnchor } from "./Cells";
import { cellRows, fmtWhen, historyEvents, runScore, taskOf } from "./model";

const Eyebrow = ({ children }: { children: ReactNode }) => <div className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">{children}</div>;
const Card = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <section data-testid={testId} className="flex flex-col gap-3 rounded-md border border-bd bg-card p-4">
    {children}
  </section>
);

const GRID: Record<string, { bg: string; bd: string }> = {
  failed: { bg: "var(--err)", bd: "var(--err)" },
  idle: { bg: "transparent", bd: "var(--bd2)" },
  passed: { bg: "var(--acc)", bd: "var(--acc)" },
};

/** «автопроверки»: счёт и клетки — из прогона версии. */
export function AutochecksCard({ view }: { view: SubmissionView }) {
  const s = runScore(view);
  return (
    <Card testId="autochecks">
      <div className="flex items-baseline justify-between">
        <Eyebrow>автопроверки</Eyebrow>
        <span className="font-mono text-[15px] font-bold" style={{ color: s && s.passed === s.total ? "var(--ok)" : "var(--err)" }}>
          {s ? `${s.passed} / ${s.total}` : "—"}
        </span>
      </div>
      {s && (
        <div className="grid grid-cols-7 gap-[5px]">
          {s.cells.map((st, i) => (
            <span key={i} data-cell={st} title={`задача ${i + 1}`} className="aspect-square rounded-[3px]" style={{ background: GRID[st]!.bg, border: `1px solid ${GRID[st]!.bd}` }} />
          ))}
        </div>
      )}
      <p className="m-0 text-[12px] leading-[1.5] text-ink2 text-pretty">Зелёный раннер не равен принятой работе: ментор смотрит, как получен результат.</p>
    </Card>
  );
}

/** «все комментарии»: по корню на строку, клик — к ячейке. */
export function AllComments({ view }: { view: SubmissionView }) {
  const rows = cellRows(view).filter((r) => r.threads.length > 0);
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <Card testId="all-comments">
      <Eyebrow>все комментарии</Eyebrow>
      {rows.length === 0 ? (
        <p className="m-0 text-[12.5px] leading-[1.5] text-ink2">Пока пусто — список соберётся из комментариев ментора к ячейкам.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rows.flatMap((r) =>
            r.threads.map((t) => (
              <button key={t.root.id} type="button" data-cell={r.cellId} onClick={() => jump(cellAnchor(r))} className="grid cursor-pointer grid-cols-[62px_minmax(0,1fr)] items-baseline gap-2 rounded-sm border border-bd bg-transparent px-2.5 py-1.5 text-left hover:border-bd2 hover:bg-card2" style={{ color: "var(--ink2)" }}>
                <span className="font-mono text-[10.5px] text-accdim">ячейка {taskOf(t.root) ?? r.cellId}</span>
                <span className="truncate text-[12.5px]">{t.root.todo || t.root.body.split("\n")[0]}</span>
              </button>
            )),
          )}
        </div>
      )}
    </Card>
  );
}

/** «история»: отправлено → вердикт → ответы, по версиям. */
export function HistoryCard({ list, view }: { list: SubmissionBrief[]; view: SubmissionView }) {
  const events = historyEvents(list, view, view.student.name);
  return (
    <Card testId="history">
      <Eyebrow>история</Eyebrow>
      <div className="flex flex-col gap-2">
        {events.map((e, i) => (
          <div key={i} className="grid grid-cols-[82px_minmax(0,1fr)] items-baseline gap-2.5">
            <span className="font-mono text-[11px] text-ink2">{fmtWhen(e.at)}</span>
            <span className="text-[12.5px] leading-[1.5] text-ink text-pretty">{e.text}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
